import { z } from "zod";
/**
 * Plans and grants (DESIGN.md, "Access and plans").
 *
 * Anyone may sign up, and a new workspace gets the open plan. Staff give a
 * person access on a plan of their own, and change or end any workspace's
 * grant; the grant decides how many questions it may hold. Which engines are
 * asked is the server's (`CAPTURE_ENGINES`), the same for every workspace, and
 * the catalog's `engines` is that list. Money is integer cents.
 *
 * The Rust API answers in exactly these shapes (`server/crates/api/src/modules/
 * grants`).
 */
export const billingSchema = z.enum(["monthly", "annual"]);
export const tierIdSchema = z.enum(["starter", "growth", "pro", "scale"]);
export const planCatalogSchema = z.object({
    tiers: z.array(z.object({ id: tierIdSchema, name: z.string(), prompts: z.number().int() })),
    engines: z.array(z.object({
        id: z.string(),
        name: z.string(),
        isPopular: z.boolean(),
        isAvailable: z.boolean(),
    })),
    annualDiscountPercent: z.number().int(),
    currency: z.string(),
});
/** What a grant is doing today. */
export const grantStandingSchema = z.enum(["active", "read_only", "locked", "none"]);
export const onExpirySchema = z.enum(["read_only", "locked"]);
export const grantSchema = z.object({
    /** Null when staff gave a number of questions that is no tier. */
    tier: tierIdSchema.nullable(),
    prompts: z.number().int(),
    engines: z.array(z.string()),
    billing: billingSchema,
    monthlyCents: z.number().int(),
    currency: z.string(),
    startsAt: z.string(),
    /** Null is no end date. */
    endsAt: z.string().nullable(),
    onExpiry: onExpirySchema,
    /**
     * `legacy` is the Founding grant every workspace from before plans has;
     * `open` is what a workspace gets on sign-up; `manual` is staff's.
     */
    source: z.enum(["manual", "legacy", "payment", "open"]),
});
export const subscriptionSchema = z.object({
    standing: grantStandingSchema,
    grant: grantSchema.nullable(),
    promptsUsed: z.number().int(),
});
export const accountSchema = z.object({
    workspaceId: z.string(),
    workspaceName: z.string(),
    createdAt: z.string(),
    standing: grantStandingSchema,
    grant: grantSchema.nullable(),
});
export const accountDetailSchema = z.object({
    account: accountSchema,
    promptsUsed: z.number().int(),
    owners: z.array(z.object({ email: z.string(), name: z.string() })),
    history: z.array(z.object({
        action: z.enum(["created", "changed", "ended"]),
        actorEmail: z.string().nullable(),
        detail: z.record(z.string(), z.unknown()),
        at: z.string(),
    })),
    /** From `usage_events`: people this week, and when anyone last used it (null: not since recording began). */
    usage: z.object({
        peopleThisWeek: z.number().int().nonnegative(),
        lastUsedAt: z.string().nullable(),
    }),
});
/** What giving access did. `emailed` false means tell the person another way. */
export const givenSchema = z.object({ workspaceId: z.string(), emailed: z.boolean() });
//# sourceMappingURL=plans.js.map